/** Background metadata cache scoped to the authenticated user and tenant. */
import { libraryMetadataStore } from './libraryMetadataStore';
import { apiFetch } from './api';
import { getPrivateCacheScope, isCurrentPrivateCacheScope, subscribePrivateCacheScope } from './privateCacheScope.js';
const SYNC_INTERVAL_MS = 5 * 60 * 1000;
const STALE_THRESHOLD_MS = 60 * 1000;
let isInitialized = false;
let lastSyncTime = null;
let syncIntervalId = null;
let invalidateTimer = null;
let cachedAggregations = null;
let activeSync = null;
let lifecycle = 0;
const subscribers = new Set();
const emptyAggregations = () => ({file_types: [], cameras: [], lenses: [], ranges: {
  focal_length: {min: null, max: null}, iso: {min: null, max: null}, date: {min: null, max: null}
}, total_images: 0});
function capture() { return {...getPrivateCacheScope(), lifecycle}; }
function current(snapshot) { return snapshot.scope && snapshot.lifecycle === lifecycle && isCurrentPrivateCacheScope(snapshot); }

export async function initialize() {
  const snapshot = capture();
  if (isInitialized || !snapshot.scope || !libraryMetadataStore.isSupported()) return;
  try {
    await libraryMetadataStore.openDatabase();
    if (!current(snapshot) || isInitialized) return;
    isInitialized = true;
    void sync();
    syncIntervalId = setInterval(() => { void sync(); }, SYNC_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  } catch (error) {
    if (current(snapshot)) console.error('[metadataSyncManager] Failed to initialize:', error);
  }
}
export function destroy() {
  lifecycle++;
  activeSync?.controller.abort();
  activeSync = null;
  clearInterval(syncIntervalId);
  clearTimeout(invalidateTimer);
  syncIntervalId = invalidateTimer = null;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  isInitialized = false;
  lastSyncTime = null;
  cachedAggregations = null;
  subscribers.clear();
}
subscribePrivateCacheScope(destroy);
function handleVisibilityChange() {
  if (document.visibilityState === 'visible' && (!lastSyncTime || Date.now() - lastSyncTime > STALE_THRESHOLD_MS)) void sync();
}
export async function sync(force = false) {
  const snapshot = capture();
  if (!snapshot.scope || activeSync) return;
  // initialize owns the first sync, avoiding two requests racing its cursor.
  if (!isInitialized) { await initialize(); return; }
  const run = {controller: new AbortController()};
  activeSync = run;
  try {
    let cursor = force ? null : await libraryMetadataStore.getLastUpdatedAt();
    let hasMore = true;
    let totalSynced = 0;
    while (hasMore && current(snapshot)) {
      const params = new URLSearchParams({limit: 1000});
      if (cursor) params.set('updated_since', cursor);
      const response = await apiFetch(`/api/images/filter-metadata?${params}`, {signal: run.controller.signal});
      if (!current(snapshot)) return;
      if (!response.ok) throw new Error(`Sync failed: ${response.status}`);
      const data = await response.json();
      if (!current(snapshot)) return;
      const images = data.images || [];
      if (images.length) {
        await libraryMetadataStore.upsertMetadata(images);
        if (!current(snapshot)) return;
        totalSynced += images.length;
        cursor = data.next_cursor;
      }
      hasMore = data.has_more && images.length > 0;
      notifySubscribers({type: 'progress', synced: totalSynced});
    }
    if (!current(snapshot)) return;
    const aggregates = await libraryMetadataStore.getAggregations();
    if (!current(snapshot)) return;
    cachedAggregations = aggregates;
    lastSyncTime = Date.now();
    notifySubscribers({type: 'complete', synced: totalSynced, aggregations: aggregates});
  } catch (error) {
    if (current(snapshot) && error.name !== 'AbortError') notifySubscribers({type: 'error', error});
  } finally {
    if (activeSync === run) activeSync = null;
  }
}
export async function fullResync() {
  lifecycle++;
  const snapshot = capture();
  if (!snapshot.scope) return;
  activeSync?.controller.abort();
  activeSync = null;
  await libraryMetadataStore.clearAll();
  if (!current(snapshot)) return;
  cachedAggregations = null;
  lastSyncTime = null;
  await sync(true);
}
export async function getAggregations() {
  const snapshot = capture();
  if (!snapshot.scope) return emptyAggregations();
  if (cachedAggregations) return cachedAggregations;
  if (!isInitialized) void initialize();
  try {
    if (libraryMetadataStore.isSupported()) {
      await libraryMetadataStore.openDatabase();
      if (!current(snapshot)) return emptyAggregations();
      const count = await libraryMetadataStore.getCount();
      if (!current(snapshot)) return emptyAggregations();
      if (count > 0) {
        const aggregates = await libraryMetadataStore.getAggregations();
        if (!current(snapshot)) return emptyAggregations();
        cachedAggregations = aggregates;
        return aggregates;
      }
    }
    const response = await apiFetch('/api/images/library-metadata');
    if (!current(snapshot)) return emptyAggregations();
    if (response.ok) {
      const data = await response.json();
      if (!current(snapshot)) return emptyAggregations();
      cachedAggregations = data;
      return data;
    }
  } catch (error) {
    if (current(snapshot)) console.error('[metadataSyncManager] Failed to fetch aggregations:', error);
  }
  return emptyAggregations();
}
export async function getFilteredIds(filters) {
  const snapshot = capture();
  if (!snapshot.scope) return [];
  const ids = await libraryMetadataStore.getFilteredIds(filters);
  return current(snapshot) ? ids : [];
}
export function subscribe(callback) { subscribers.add(callback); return () => subscribers.delete(callback); }
function notifySubscribers(event) {
  subscribers.forEach(callback => { try { callback(event); } catch {} });
}
export function isReady() { return isInitialized && cachedAggregations !== null; }
export function getStatus() { return {initialized: isInitialized, syncing: Boolean(activeSync), lastSyncTime, hasData: cachedAggregations !== null}; }
export function invalidateCache() {
  cachedAggregations = null;
  const snapshot = capture();
  clearTimeout(invalidateTimer);
  invalidateTimer = setTimeout(() => { if (current(snapshot)) void sync(); }, 2000);
}
export const metadataSyncManager = {initialize, destroy, sync, fullResync, getAggregations, getFilteredIds, subscribe, isReady, getStatus, invalidateCache};
